import React, { useState } from 'react';
import {
  Button,
  CandidateAccolades,
  CandidatePerformanceFeedback,
  CandidateReputationDoNotShowBanner,
  Card,
  CardFooter,
  CardHeader,
  CardSection,
  CondensedLarge,
  Gutter,
  Layout,
  LegalTechSkillsDisplay,
  Paragraph,
  SmallHeader,
  Suspense,
  TalentCertifications,
  TalentEducation,
  TalentExperiences,
  TalentLanguages,
  TalentQualifications,
  ToastUtil,
} from '@axiom/ui';
import { Certification } from '@axiom/const';
import type {
  Candidate,
  CandidateLegalTechSkill,
  GroupedExperiences,
  LimitedCandidate,
  Taxonomy,
  User,
} from '@axiom/validation';

import { isAFCUser, isLoggedInUser, isLpUser } from '../../utils/roles-util';
import { usePublicEngagementToLoginModal } from '../../hooks/usePublicEngagementToLoginModal';
import { WindowUtil } from '../../utils/window-util';
import { CandidateBios } from '../CandidateBios/CandidateBios';
import { TalentCardContent } from '../TalentCardContent/TalentCardContent';

import { TalentDetailsRateLimitInfo } from './TalentDetailsRateLimitInfo';

const { QUALIFICATION_AWARDS } = Certification;

export type TalentDetailsProps = {
  candidate: LimitedCandidate;
  groupedExperiences: GroupedExperiences[];
  user: User | null;
  taxonomy: Taxonomy;
  legalTechSkills?: CandidateLegalTechSkill[] | null;
  setInquiredCandidate: React.Dispatch<
    React.SetStateAction<LimitedCandidate | Candidate | null>
  >;
  isPricingShown?: boolean;
};

export const TalentDetails = ({
  candidate,
  groupedExperiences,
  user,
  taxonomy,
  legalTechSkills,
  setInquiredCandidate,
  isPricingShown,
}: TalentDetailsProps) => {
  const [downloadPdf, setDownloadPdf] = useState(false);
  const { toggleLoginModal, LoginModalComponent } =
    usePublicEngagementToLoginModal();

  const isUserLoggedIn = !!(user && isLoggedInUser(user));
  const isAuthorized = !!(user && isAFCUser(user));

  const awardTypes = Object.values(QUALIFICATION_AWARDS);
  const calculateExistenceOfCertificationsAndQualifications = (
    candidate.certifications ?? []
  ).reduce(
    (acc, cert) => {
      if (awardTypes.includes(cert.award)) {
        acc.hasQualifications = true;
      } else {
        acc.hasCertifications = true;
      }
      return acc;
    },
    { hasCertifications: false, hasQualifications: false }
  );
  const { hasCertifications, hasQualifications } =
    calculateExistenceOfCertificationsAndQualifications;
  const showReputation =
    candidate.showReputation === true || candidate.showReputation === null;

  const handleDownloadClick = () => {
    setDownloadPdf(true);

    const toastName = 'DOWNLOAD_BIO_PDF';
    const toasts = ToastUtil.getAll();
    const alreadyOpen = toasts.some(toast => toast.name === toastName);
    if (!alreadyOpen) {
      ToastUtil.add({
        name: toastName,
        type: 'info',
        children: (
          <CondensedLarge>
            Your PDF is being created - it will download automatically when
            ready.
          </CondensedLarge>
        ),
      });
    }
  };

  return (
    <>
      <Card name="TALENT_DETAILS_CARD">
        <TalentCardContent
          candidate={candidate}
          user={user}
          taxonomy={taxonomy}
          isPricingShown={isPricingShown}
          onLoggedOutRequestInterview={() => {
            toggleLoginModal(true);
          }}
        />
        <CardFooter stackableOn="mobile">
          {isUserLoggedIn && !isLpUser(user) ? (
            <Button
              name="DOWNLOAD_BIO"
              variation="outline"
              onClick={() => {
                handleDownloadClick();
              }}
            >
              Download
            </Button>
          ) : null}
          <Button
            name="INQUIRE"
            variation="outline"
            onClick={() => {
              setInquiredCandidate(candidate);
            }}
          >
            Contact Axiom
          </Button>
        </CardFooter>
      </Card>
      <Gutter bottom="16px" />
      {candidate.candidateReputation ? (
        <Gutter bottom="16px">
          <Card name="TALENT_DETAILS_AXIOM_PERFORMANCE">
            <CardHeader>
              <SmallHeader name="TALENT_DETAILS_AXIOM_PERFORMANCE_HEADER">
                Axiom Performance
              </SmallHeader>
            </CardHeader>
            <CardSection>
              <Layout direction="vertical">
                <CandidateAccolades
                  candidateReputation={candidate.candidateReputation}
                />
                {showReputation &&
                candidate.candidateReputation.displayCandidateReputation ? (
                  <Gutter top="24px">
                    <CandidatePerformanceFeedback
                      stacked
                      candidateReputation={candidate.candidateReputation}
                    />
                  </Gutter>
                ) : (
                  <CandidateReputationDoNotShowBanner
                    name={
                      showReputation
                        ? 'limitedFeedbackDisplay'
                        : 'optedOutDisplay'
                    }
                  />
                )}
              </Layout>
            </CardSection>
          </Card>
        </Gutter>
      ) : null}
      {!isUserLoggedIn && (
        <Gutter bottom="16px">
          <Card name="TALENT_DETAILS_SIGN_IN">
            <CardHeader>
              <SmallHeader>Experiences</SmallHeader>
            </CardHeader>
            <CardSection divider>
              <Paragraph>
                Sign in to see {candidate.calculatedDisplayName}'s experience.
              </Paragraph>
            </CardSection>
            <CardFooter>
              <Button
                name="SIGN_IN"
                onClick={() => {
                  WindowUtil.toLogin();
                }}
              >
                Sign In
              </Button>
            </CardFooter>
          </Card>
        </Gutter>
      )}
      {isAuthorized && !!candidate.restricted ? (
        <Gutter bottom="16px">
          <TalentDetailsRateLimitInfo />
        </Gutter>
      ) : null}
      {groupedExperiences.length > 0 && (
        <Gutter bottom="16px">
          <Card name="TALENT_DETAILS_EXPERIENCES">
            <CardHeader>
              <SmallHeader>Experience</SmallHeader>
            </CardHeader>
            <TalentExperiences groupedExperiences={groupedExperiences} />
          </Card>
        </Gutter>
      )}
      {!!legalTechSkills && legalTechSkills.length > 0 && (
        <Gutter bottom="16px">
          <Card name="TALENT_DETAILS_LEGAL_TECHNICAL_SKILLS">
            <CardHeader>
              <SmallHeader>Legal Tech Skills</SmallHeader>
            </CardHeader>
            <CardSection>
              <LegalTechSkillsDisplay
                legalTechSkills={legalTechSkills}
                taxonomy={taxonomy}
              />
            </CardSection>
          </Card>
        </Gutter>
      )}
      {(candidate.degrees?.length ?? 0) > 0 && (
        <Gutter bottom="16px">
          <Card name="TALENT_DETAILS_EDUCATION">
            <CardHeader>
              <SmallHeader>Education</SmallHeader>
            </CardHeader>
            <CardSection>
              <TalentEducation candidate={candidate} />
            </CardSection>
          </Card>
        </Gutter>
      )}
      {(candidate.certifications?.length ?? 0) > 0 && (
        <Gutter bottom="16px">
          <Card name="TALENT_DETAILS_ACCOMPLISHMENTS">
            <CardHeader>
              <SmallHeader>Accomplishments</SmallHeader>
            </CardHeader>
            {hasCertifications ? (
              <CardSection
                divider
                name="TALENT_DETAILS_CERTIFICATIONS_SUBSECTION"
              >
                <TalentCertifications candidate={candidate} />
              </CardSection>
            ) : null}
            {hasQualifications ? (
              <CardSection name="TALENT_DETAILS_QUALIFICATIONS_SUBSECTION">
                <TalentQualifications candidate={candidate} />
              </CardSection>
            ) : null}
          </Card>
        </Gutter>
      )}
      {(candidate.languages?.length ?? 0) > 0 && (
        <Gutter bottom="16px">
          <Card name="TALENT_DETAILS_LANGUAGES">
            <CardHeader>
              <SmallHeader>Languages</SmallHeader>
            </CardHeader>
            <CardSection>
              <TalentLanguages candidate={candidate} />
            </CardSection>
          </Card>
        </Gutter>
      )}
      {downloadPdf ? (
        <Suspense fallback={null}>
          <div className="k-pdf-export" data-test="bio-wrapper">
            <CandidateBios
              onSave={() => {
                setDownloadPdf(!downloadPdf);
              }}
              isDownloading
              candidateId={candidate.id}
            />
          </div>
        </Suspense>
      ) : null}
      <LoginModalComponent user={user} />
    </>
  );
};
